import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownLeft, Plus, Trash2,
    Loader2, X, Search, Filter, Download, Edit, Save, FileText, Clock, CheckCircle,
    AlertTriangle, Calendar, BarChart3, Tag
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';

const DEFAULT_CATEGORIES = ['Comissões', 'Infraestrutura', 'Serviços', 'Marketing', 'Pessoal', 'Geral'];
const STATUS_OPTIONS = [
    { value: 'pago', label: 'Pago', color: '#22c55e' },
    { value: 'pendente', label: 'Pendente', color: '#f59e0b' },
    { value: 'cancelado', label: 'Cancelado', color: '#ef4444' },
];

export default function ManagementPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get('tab') || 'resumo';
    const action = searchParams.get('action');

    const [data, setData] = useState({ entries: [], income: 0, expense: 0, balance: 0 });
    const [summary, setSummary] = useState({ categories: {}, monthly: {}, pending: [], total_entries: 0 });
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [form, setForm] = useState({ description: '', type: 'income', value: '', category: 'Geral', status: 'pago', notes: '', due_date: '' });
    const [saving, setSaving] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [newCatName, setNewCatName] = useState('');

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const setTab = (t) => setSearchParams({ tab: t });

    const fetchLedger = useCallback(async () => {
        try {
            const res = await fetch(`${API}/ledger`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setData(await res.json());
        } catch {} finally { setLoading(false); }
    }, []);

    const fetchSummary = useCallback(async () => {
        try {
            const res = await fetch(`${API}/ledger/summary`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setSummary(await res.json());
        } catch {}
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await fetch(`${API}/ledger/categories`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) {
                const cats = await res.json();
                setCategories([...new Set([...DEFAULT_CATEGORIES, ...cats.map(c => c.name)])]);
            }
        } catch {}
    }, []);

    useEffect(() => { fetchLedger(); fetchSummary(); fetchCategories(); }, [fetchLedger, fetchSummary, fetchCategories]);

    useEffect(() => {
        if (action === 'novo_lançamento') { openNewModal(); setSearchParams({ tab: 'financeiro' }); }
    }, [action]);

    const openNewModal = () => {
        setEditingEntry(null);
        setForm({ description: '', type: 'income', value: '', category: 'Geral', status: 'pago', notes: '', due_date: '' });
        setShowModal(true);
    };

    const openEditModal = (entry) => {
        setEditingEntry(entry);
        setForm({
            description: entry.description, type: entry.type, value: String(entry.value),
            category: entry.category || 'Geral', status: entry.status || 'pago',
            notes: entry.notes || '', due_date: entry.due_date || ''
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.description.trim() || !form.value) { toast.error('Preencha descrição e valor'); return; }
        setSaving(true);
        try {
            const body = { ...form, value: parseFloat(form.value) };
            const url = editingEntry ? `${API}/ledger/${editingEntry.ledger_id}` : `${API}/ledger`;
            const method = editingEntry ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
            if (res.ok) {
                toast.success(editingEntry ? 'Lançamento atualizado' : 'Lançamento criado');
                setShowModal(false);
                fetchLedger(); fetchSummary();
            } else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro de conexão'); } finally { setSaving(false); }
    };

    const deleteEntry = async (id) => {
        if (!window.confirm('Excluir lançamento?')) return;
        const res = await fetch(`${API}/ledger/${id}`, { method: 'DELETE', headers });
        if (res.ok) { toast.success('Excluido'); fetchLedger(); fetchSummary(); }
    };

    const exportCSV = async () => {
        try {
            const res = await fetch(`${API}/ledger/export`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) {
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = 'gestão_export.csv'; a.click();
                toast.success('Exportacao concluida');
            }
        } catch { toast.error('Erro ao exportar'); }
    };

    const addCategory = async () => {
        if (!newCatName.trim()) return;
        try {
            const res = await fetch(`${API}/ledger/categories`, { method: 'POST', headers, body: JSON.stringify({ name: newCatName.trim() }) });
            if (res.ok) { toast.success('Categoria criada'); setNewCatName(''); fetchCategories(); }
        } catch { toast.error('Erro'); }
    };

    const deleteCategory = async (name) => {
        if (!window.confirm(`Excluir categoria "${name}"?`)) return;
        const res = await fetch(`${API}/ledger/categories/${encodeURIComponent(name)}`, { method: 'DELETE', headers });
        if (res.ok) { toast.success('Excluida'); fetchCategories(); }
    };

    const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

    const filteredEntries = useMemo(() => {
        return data.entries.filter(e => {
            if (filterType !== 'all' && e.type !== filterType) return false;
            if (filterStatus !== 'all' && (e.status || 'pago') !== filterStatus) return false;
            if (filterCategory !== 'all' && e.category !== filterCategory) return false;
            if (searchText && !e.description.toLowerCase().includes(searchText.toLowerCase())) return false;
            return true;
        });
    }, [data.entries, filterType, filterStatus, filterCategory, searchText]);

    const monthlyData = useMemo(() => {
        const m = summary.monthly || {};
        return Object.entries(m).slice(-6).map(([k, v]) => ({
            month: k.slice(5),
            income: v.income || 0,
            expense: v.expense || 0,
        }));
    }, [summary.monthly]);

    const maxMonthly = useMemo(() => Math.max(1, ...monthlyData.map(m => Math.max(m.income, m.expense))), [monthlyData]);

    const TABS = [
        { key: 'resumo', label: 'Resumo', icon: BarChart3 },
        { key: 'financeiro', label: 'Financeiro', icon: DollarSign },
        { key: 'contas', label: 'Contas', icon: Tag },
        { key: 'auditoria', label: 'Auditoria', icon: Clock },
    ];

    return (
        <div className="sv-page" data-testid="management-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Gestão</h2>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Financeiro e operacional</p>
                </div>
                <div className="flex gap-2">
                    <button className="sv-btn-system" onClick={exportCSV} data-testid="export-btn"><Download size={14} /> Exportar</button>
                    <button className="sv-btn-gold-system sv-btn-system" onClick={openNewModal} data-testid="new-entry-btn"><Plus size={14} /> Novo Lançamento</button>
                </div>
            </div>

            <div className="sv-tabs" data-testid="management-tabs">
                {TABS.map(t => (
                    <button key={t.key} className={`sv-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
                        <t.icon size={12} /> {t.label}
                    </button>
                ))}
            </div>

            {/* KPIs always visible */}
            <div className="sv-kpi-grid" data-testid="management-kpis">
                <div className="sv-kpi"><span className="sv-kpi-label">Receita</span><span className="sv-kpi-value" style={{ color: '#22c55e' }}>{fmt(data.income)}</span></div>
                <div className="sv-kpi"><span className="sv-kpi-label">Despesas</span><span className="sv-kpi-value" style={{ color: '#ef4444' }}>{fmt(data.expense)}</span></div>
                <div className="sv-kpi"><span className="sv-kpi-label">Saldo</span><span className="sv-kpi-value">{fmt(data.balance)}</span><span className={`sv-kpi-delta ${data.balance >= 0 ? 'up' : 'down'}`}>{data.balance >= 0 ? 'Positivo' : 'Negativo'}</span></div>
                <div className="sv-kpi"><span className="sv-kpi-label">Pendentes</span><span className="sv-kpi-value" style={{ color: '#f59e0b' }}>{summary.pending?.length || 0}</span></div>
            </div>

            {/* TAB: RESUMO */}
            {tab === 'resumo' && (
                <div className="sv-bento-grid" data-testid="tab-resumo">
                    <div className="sv-card-system">
                        <div className="sv-card-header"><span className="sv-card-label">Evolucao Mensal</span></div>
                        {monthlyData.length === 0 ? (
                            <p className="text-sm py-6 text-center" style={{ color: 'var(--muted)' }}>Sem dados mensais ainda</p>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {monthlyData.map((m, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <span className="text-xs w-8 font-medium" style={{ color: 'var(--muted)' }}>{m.month}</span>
                                        <div className="flex-1 flex gap-1">
                                            <div className="h-5 rounded-r" style={{ width: `${(m.income / maxMonthly) * 100}%`, background: '#22c55e', minWidth: m.income > 0 ? 4 : 0 }} />
                                            <div className="h-5 rounded-r" style={{ width: `${(m.expense / maxMonthly) * 100}%`, background: '#ef4444', minWidth: m.expense > 0 ? 4 : 0 }} />
                                        </div>
                                        <span className="text-[10px] w-24 text-right" style={{ color: 'var(--muted-2)' }}>{fmt(m.income - m.expense)}</span>
                                    </div>
                                ))}
                                <div className="flex gap-4 mt-2 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                                    <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#22c55e' }} />Receita</span>
                                    <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#ef4444' }} />Despesa</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="sv-card-system">
                        <div className="sv-card-header"><span className="sv-card-label">Por Categoria</span></div>
                        {Object.keys(summary.categories || {}).length === 0 ? (
                            <p className="text-sm py-6 text-center" style={{ color: 'var(--muted)' }}>Sem dados</p>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {Object.entries(summary.categories).map(([cat, vals]) => (
                                    <div key={cat} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                                        <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{cat}</span>
                                        <div className="flex gap-3 text-xs">
                                            <span style={{ color: '#22c55e' }}>+{fmt(vals.income || 0)}</span>
                                            <span style={{ color: '#ef4444' }}>-{fmt(vals.expense || 0)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {(summary.pending || []).length > 0 && (
                        <div className="sv-card-system" style={{ gridColumn: '1 / -1' }}>
                            <div className="sv-card-header"><span className="sv-card-label">Pendentes ({summary.pending.length})</span></div>
                            <div className="flex flex-col gap-2">
                                {summary.pending.slice(0, 5).map(e => (
                                    <div key={e.ledger_id} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle size={14} style={{ color: '#f59e0b' }} />
                                            <span className="text-sm" style={{ color: 'var(--text)' }}>{e.description}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {e.due_date && <span className="text-xs" style={{ color: 'var(--muted-2)' }}>{e.due_date}</span>}
                                            <span className="text-sm font-bold" style={{ color: e.type === 'income' ? '#22c55e' : '#ef4444' }}>{fmt(e.value)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* TAB: FINANCEIRO */}
            {tab === 'financeiro' && (
                <div data-testid="tab-financeiro">
                    <div className="sv-card-system mb-3">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="relative flex-1 min-w-[180px]">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-2)' }} />
                                <input type="text" placeholder="Buscar lançamento..." value={searchText} onChange={e => setSearchText(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="ledger-search" />
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {[['all', 'Todos'], ['income', 'Receitas'], ['expense', 'Despesas']].map(([k, l]) => (
                                    <button key={k} className={`sv-subnav-chip ${filterType === k ? 'active' : ''}`} onClick={() => setFilterType(k)}>{l}</button>
                                ))}
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {[['all', 'Status'], ...STATUS_OPTIONS.map(s => [s.value, s.label])].map(([k, l]) => (
                                    <button key={k} className={`sv-subnav-chip ${filterStatus === k ? 'active' : ''}`} onClick={() => setFilterStatus(k)}>{l}</button>
                                ))}
                            </div>
                            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                                className="px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="category-filter">
                                <option value="all">Todas categorias</option>
                                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="sv-card-system" style={{ padding: 0, overflow: 'hidden' }}>
                        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
                            <span className="sv-card-label">Lançamentos ({filteredEntries.length})</span>
                        </div>
                        {loading ? (
                            <div className="sv-empty-state"><Loader2 className="animate-spin" /><p className="sv-empty-state-title">Carregando...</p></div>
                        ) : filteredEntries.length === 0 ? (
                            <div className="sv-empty-state"><DollarSign /><p className="sv-empty-state-title">Nenhum lançamento</p><p className="sv-empty-state-desc">Adicione receitas e despesas</p></div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="sv-data-table" data-testid="ledger-table">
                                    <thead><tr><th>Descrição</th><th>Tipo</th><th>Categoria</th><th>Status</th><th>Data</th><th style={{ textAlign: 'right' }}>Valor</th><th style={{ width: 70 }}></th></tr></thead>
                                    <tbody>
                                        {filteredEntries.map(e => {
                                            const st = STATUS_OPTIONS.find(s => s.value === (e.status || 'pago'));
                                            return (
                                                <tr key={e.ledger_id}>
                                                    <td>
                                                        <div className="flex items-center gap-2">
                                                            {e.type === 'income' ? <ArrowUpRight size={14} className="text-green-500" /> : <ArrowDownLeft size={14} className="text-red-500" />}
                                                            <div>
                                                                <span className="font-medium">{e.description}</span>
                                                                {e.notes && <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted-2)' }}>{e.notes}</p>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td><span className={`sv-badge ${e.type === 'income' ? 'sv-badge-success' : 'sv-badge-danger'}`}>{e.type === 'income' ? 'Receita' : 'Despesa'}</span></td>
                                                    <td style={{ color: 'var(--muted)' }}>{e.category}</td>
                                                    <td><span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${st?.color}18`, color: st?.color, border: `1px solid ${st?.color}30` }}>{st?.label}</span></td>
                                                    <td style={{ color: 'var(--muted-2)', fontSize: 12 }}>
                                                        {e.created_at ? new Date(e.created_at).toLocaleDateString('pt-BR') : '-'}
                                                        {e.due_date && <div className="text-[10px]" style={{ color: '#f59e0b' }}>Venc: {e.due_date}</div>}
                                                    </td>
                                                    <td style={{ textAlign: 'right', fontWeight: 700, color: e.type === 'income' ? '#22c55e' : '#ef4444' }}>{e.type === 'income' ? '+' : '-'} {fmt(e.value)}</td>
                                                    <td>
                                                        <div className="flex gap-1">
                                                            <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => openEditModal(e)} data-testid={`edit-${e.ledger_id}`}><Edit size={11} /></button>
                                                            <button className="sv-btn-system" style={{ padding: '4px 6px', color: '#ef4444' }} onClick={() => deleteEntry(e.ledger_id)}><Trash2 size={11} /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* TAB: CONTAS */}
            {tab === 'contas' && (
                <div data-testid="tab-contas">
                    <div className="sv-card-system">
                        <div className="sv-card-header"><span className="sv-card-label">Categorias</span></div>
                        <div className="flex gap-2 mb-4">
                            <input type="text" placeholder="Nova categoria..." value={newCatName} onChange={e => setNewCatName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addCategory()}
                                className="flex-1 px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="new-cat-input" />
                            <button className="sv-btn-gold-system sv-btn-system" onClick={addCategory} data-testid="add-cat-btn"><Plus size={14} /> Adicionar</button>
                        </div>
                        <div className="flex flex-col gap-2">
                            {categories.map(cat => {
                                const catData = summary.categories?.[cat] || {};
                                const inc = catData.income || 0;
                                const exp = catData.expense || 0;
                                const bal = inc - exp;
                                return (
                                    <div key={cat} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }} data-testid={`cat-${cat}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)' }}><Tag size={14} style={{ color: 'var(--sv-gold-primary)' }} /></div>
                                            <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{cat}</span>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <div className="text-xs" style={{ color: '#22c55e' }}>+{fmt(inc)}</div>
                                                <div className="text-xs" style={{ color: '#ef4444' }}>-{fmt(exp)}</div>
                                            </div>
                                            <span className="text-sm font-bold" style={{ color: bal >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(bal)}</span>
                                            {!DEFAULT_CATEGORIES.includes(cat) && (
                                                <button className="sv-btn-system" style={{ padding: '4px 6px', color: '#ef4444' }} onClick={() => deleteCategory(cat)}><Trash2 size={11} /></button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: AUDITORIA */}
            {tab === 'auditoria' && (
                <div data-testid="tab-auditoria">
                    <div className="sv-card-system">
                        <div className="sv-card-header"><span className="sv-card-label">Histórico de Atividades</span></div>
                        {data.entries.length === 0 ? (
                            <div className="sv-empty-state"><Clock /><p className="sv-empty-state-title">Nenhuma atividade</p></div>
                        ) : (
                            <div className="flex flex-col gap-1">
                                {data.entries.map((e, i) => (
                                    <div key={e.ledger_id} className="flex items-start gap-3 py-3" style={{ borderBottom: i < data.entries.length - 1 ? '1px solid var(--border)' : 'none' }} data-testid={`audit-${e.ledger_id}`}>
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: e.type === 'income' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)' }}>
                                            {e.type === 'income' ? <ArrowUpRight size={14} style={{ color: '#22c55e' }} /> : <ArrowDownLeft size={14} style={{ color: '#ef4444' }} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{e.description}</span>
                                                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>{e.category}</span>
                                                {(() => { const s = STATUS_OPTIONS.find(x => x.value === (e.status || 'pago')); return s ? <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${s.color}18`, color: s.color }}>{s.label}</span> : null; })()}
                                            </div>
                                            {e.notes && <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{e.notes}</p>}
                                            <div className="flex gap-3 mt-1 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                                                <span><Calendar size={9} className="inline mr-1" />Criado: {e.created_at ? new Date(e.created_at).toLocaleString('pt-BR') : '-'}</span>
                                                {e.updated_at && <span><Clock size={9} className="inline mr-1" />Editado: {new Date(e.updated_at).toLocaleString('pt-BR')}</span>}
                                                {e.due_date && <span><AlertTriangle size={9} className="inline mr-1" />Venc: {e.due_date}</span>}
                                            </div>
                                        </div>
                                        <span className="text-sm font-bold flex-shrink-0" style={{ color: e.type === 'income' ? '#22c55e' : '#ef4444' }}>
                                            {e.type === 'income' ? '+' : '-'}{fmt(e.value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* MODAL */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
                    <div className="sv-card-system w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ledger-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                                {editingEntry ? 'Editar Lançamento' : 'Novo Lançamento'}
                            </h3>
                            <button className="sv-btn-system" style={{ padding: 4 }} onClick={() => setShowModal(false)}><X size={14} /></button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Tipo</label>
                                <div className="flex gap-2">
                                    <button className={`sv-subnav-chip ${form.type === 'income' ? 'active' : ''}`} onClick={() => setForm({ ...form, type: 'income' })}>Receita</button>
                                    <button className={`sv-subnav-chip ${form.type === 'expense' ? 'active' : ''}`} onClick={() => setForm({ ...form, type: 'expense' })}>Despesa</button>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Descrição</label>
                                <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Ex: Comissao Amazon"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="ledger-desc-input" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Valor (R$)</label>
                                    <input type="number" step="0.01" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="0.00"
                                        className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="ledger-value-input" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Vencimento</label>
                                    <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                                        className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="ledger-duedate-input" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Categoria</label>
                                <div className="flex gap-2 flex-wrap">
                                    {categories.map(c => (<button key={c} className={`sv-subnav-chip ${form.category === c ? 'active' : ''}`} onClick={() => setForm({ ...form, category: c })}>{c}</button>))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Status</label>
                                <div className="flex gap-2">
                                    {STATUS_OPTIONS.map(s => (<button key={s.value} className={`sv-subnav-chip ${form.status === s.value ? 'active' : ''}`} onClick={() => setForm({ ...form, status: s.value })}>{s.label}</button>))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Observacoes</label>
                                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notas adicionais..."
                                    className="w-full px-3 py-2.5 rounded-lg text-sm resize-none" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="ledger-notes-input" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button className="sv-btn-system" onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="ledger-save-btn">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : null} {editingEntry ? 'Salvar' : 'Criar Lançamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
